import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Minus, ShieldCheck, Users as UsersIcon } from 'lucide-react';
import { useState } from 'react';
import { http } from '../api/client';
import { Badge } from '../components/ui/Badge';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { TableSkeleton } from '../components/ui/Skeleton';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import type { PermissionDef, RoleRow } from '../types';

/**
 * Roles & permissions matrix.
 * Rows are permissions grouped by module, columns are roles. Toggling a cell
 * PATCHes the role and - because a permission change revokes that role's
 * sessions - the UI says so before it happens.
 */
export function RolesPage() {
  const can = useAuth((s) => s.can);
  const me = useAuth((s) => s.user);
  const toast = useUi((s) => s.toast);
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);

  const { data: catalogue, isLoading: loadingCatalogue } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => http.get<{ grouped: Array<{ module: string; permissions: PermissionDef[] }> }>('/roles/permissions'),
  });
  const { data: rolesData, isLoading: loadingRoles } = useQuery({
    queryKey: ['roles'],
    queryFn: () => http.get<{ roles: RoleRow[] }>('/roles'),
  });

  const roles = rolesData?.roles ?? [];
  const activeRole = roles.find((r) => r.id === selected) ?? null;

  const toggle = useMutation({
    mutationFn: ({ roleId, permissions }: { roleId: string; permissions: string[] }) =>
      http.patch(`/roles/${roleId}`, { permissions }),
    onSuccess: () => {
      toast({
        kind: 'success',
        title: 'Permissions updated',
        message: 'Everyone with that role has been signed out so the change takes effect immediately.',
      });
      void qc.invalidateQueries({ queryKey: ['roles'] });
      // Your own permission set may have changed, so refresh the session.
      if (activeRole?.key === me?.roleKey) void useAuth.getState().refreshUser();
    },
    onError: (err) => toast({ kind: 'error', title: 'Could not update permissions', message: (err as Error).message }),
  });

  if (loadingCatalogue || loadingRoles) return <TableSkeleton rows={10} cols={4} />;
  if (!catalogue || !roles.length) return <EmptyState title="No roles configured" />;

  const editable = can('settings:roles');

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-ink">Roles &amp; access</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Every screen and action is gated by a permission code, enforced server-side on each request.
        </p>
      </header>

      {/* Role summary cards */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {roles.map((role) => (
          <button
            key={role.id}
            onClick={() => setSelected(selected === role.id ? null : role.id)}
            aria-pressed={selected === role.id}
            className={`card p-4 text-start transition-colors ${
              selected === role.id ? 'border-brand-500 ring-2 ring-brand-500/20' : 'hover:border-brand-300'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-ink">{role.name}</p>
              {role.is_system && <Badge tone="neutral">built-in</Badge>}
            </div>
            <p className="mt-1 line-clamp-2 min-h-[2.5rem] text-xs text-ink-muted">{role.description}</p>
            <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs">
              <Badge tone="brand">{role.permissions.length} permissions</Badge>
              <Badge tone="neutral"><UsersIcon className="h-3 w-3" /> {role.user_count}</Badge>
              {Number(role.discount_limit_pct) > 0 && <Badge tone="neutral">{Number(role.discount_limit_pct)}% discount</Badge>}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-ink-faint">
              {role.void_approval && <span>void approval</span>}
              {role.refund_approval && <span>refund approval</span>}
            </div>
          </button>
        ))}
      </div>

      {/* Matrix */}
      <Card>
        <CardHeader
          title="Permission matrix"
          subtitle={activeRole
            ? `Editing ${activeRole.name} — click a cell to grant or revoke`
            : 'Select a role above to edit it. Cells show grant, revoke or inherit.'}
          icon={<ShieldCheck className="h-4 w-4" />}
          action={activeRole && editable ? (
            <Badge tone="warning">changes sign that role out</Badge>
          ) : undefined}
        />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-line">
                <th scope="col" className="sticky start-0 z-10 bg-surface-1 px-4 py-3 text-start text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  Permission
                </th>
                {roles.map((r) => (
                  <th key={r.id} scope="col" className="px-2 py-3 text-center text-xs font-semibold text-ink-muted">
                    <button
                      onClick={() => setSelected(selected === r.id ? null : r.id)}
                      className={`mx-auto block max-w-[90px] truncate rounded px-1 py-0.5 transition-colors ${
                        selected === r.id ? 'bg-brand-600 text-white' : 'hover:bg-surface-2'
                      }`}
                      title={r.name}
                    >
                      {r.key}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            {catalogue.grouped.map((group) => (
              <tbody key={group.module}>
                <tr>
                  <td colSpan={roles.length + 1} className="border-b border-line bg-surface-0 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-faint">
                    {group.module}
                  </td>
                </tr>
                {group.permissions.map((perm) => (
                  <tr key={perm.code} className="border-b border-line last:border-0 hover:bg-surface-0">
                    <th scope="row" className="sticky start-0 z-10 bg-surface-1 px-4 py-2.5 text-start font-normal">
                      <span className="block text-ink">{perm.name}</span>
                      <span className="block font-mono text-[11px] text-ink-faint">{perm.code}</span>
                    </th>
                    {roles.map((role) => {
                      const granted = role.permissions.includes(perm.code);
                      const isEditable = editable && selected === role.id;
                      const isSelfRole = role.key === me?.roleKey;
                      return (
                        <td key={role.id} className="px-2 py-2.5 text-center">
                          <button
                            disabled={!isEditable}
                            aria-label={`${granted ? 'Revoke' : 'Grant'} ${perm.code} for ${role.name}`}
                            aria-pressed={granted}
                            onClick={() => {
                              const next = granted
                                ? role.permissions.filter((c) => c !== perm.code)
                                : [...role.permissions, perm.code];
                              toggle.mutate({ roleId: role.id, permissions: next });
                            }}
                            className={`mx-auto flex h-7 w-7 items-center justify-center rounded-md border transition-colors ${
                              granted
                                ? 'border-emerald-300 bg-emerald-50 text-emerald-600 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                : 'border-line bg-surface-0 text-ink-faint'
                            } ${isEditable ? 'cursor-pointer hover:border-brand-400' : 'cursor-default opacity-90'} ${
                              isSelfRole && !granted ? 'ring-1 ring-amber-400' : ''
                            }`}
                          >
                            {granted ? <Check className="h-4 w-4" /> : <Minus className="h-3.5 w-3.5" />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-4 border-t border-line px-4 py-3 text-xs text-ink-muted">
          <span className="flex items-center gap-1.5">
            <span className="flex h-4 w-4 items-center justify-center rounded border border-emerald-300 bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30"><Check className="h-3 w-3" /></span>
            granted
          </span>
          <span className="flex items-center gap-1.5">
            <span className="flex h-4 w-4 items-center justify-center rounded border border-line bg-surface-0 text-ink-faint"><Minus className="h-3 w-3" /></span>
            not granted
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-4 w-4 rounded ring-1 ring-amber-400" />
            your own role — you cannot remove your own admin access
          </span>
        </div>
      </Card>

      {!editable && (
        <Card>
          <CardBody>
            <p className="text-sm text-ink-muted">
              You can see this matrix but not change it. Ask an owner to adjust permissions —
              every change is written to the audit log with the before/after diff.
            </p>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
